import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import type { SxProps, Theme } from "@mui/material/styles";
import { alpha, useTheme } from "@mui/material/styles";
import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import type { SearchProcedure, SearchResultItem } from "@/services/search";
import { searchProcedures } from "@/services/search";
import type { SearchHistoryItem } from "@/stores/searchHistory";
import { useSearchHistoryStore } from "@/stores/searchHistory";

import SearchRecents from "./SearchRecents";
import SearchResults from "./SearchResults";

interface GroupedSearchResults {
    specialties: SearchResultItem[];
    proceduresBySpecialty: { [specialtyName: string]: SearchResultItem[] };
}

export interface SearchHandle {
    /**
     * Append a character to the search query without focusing the input
     */
    appendToQuery: (char: string) => void;
    /**
     * Remove the last character from the search query
     */
    removeLastChar: () => void;
    /**
     * Focus the search input
     */
    focus: () => void;
}

export const Search = forwardRef<
    SearchHandle,
    {
        placeholder?: string;
        sx?: SxProps<Theme>;
    } & React.ComponentProps<typeof TextField>
>(function Search({ sx, ...props }, ref) {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const [query, setQuery] = useState("");
    const [results, setResults] = useState<SearchProcedure[]>([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const popperRef = useRef<HTMLDivElement>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    const { history, addToHistory, removeFromHistory } =
        useSearchHistoryStore();

    // Group results by specialty for procedures, and collect specialties separately
    const groupedResults = useMemo((): GroupedSearchResults => {
        const specialties: SearchResultItem[] = [];
        const proceduresBySpecialty: {
            [specialtyName: string]: SearchResultItem[];
        } = {};

        results.forEach((procedure) => {
            procedure.specialties.forEach((specialty) => {
                // Collect unique specialties
                const specialtyExists = specialties.some(
                    (item) =>
                        item.type === "specialty" &&
                        item.slug === specialty.slug,
                );
                if (!specialtyExists) {
                    specialties.push({
                        type: "specialty",
                        specialty,
                        label: specialty.name,
                        slug: specialty.slug,
                    });
                }

                // Group procedures by specialty
                if (!proceduresBySpecialty[specialty.name]) {
                    proceduresBySpecialty[specialty.name] = [];
                }

                // Add procedure if not already present
                const procedureExists = proceduresBySpecialty[
                    specialty.name
                ].some(
                    (item) =>
                        item.type === "procedure" &&
                        item.slug === procedure.slug,
                );
                if (!procedureExists) {
                    proceduresBySpecialty[specialty.name].push({
                        type: "procedure",
                        procedure,
                        specialty,
                        label: procedure.title,
                        slug: procedure.slug,
                    });
                }
            });
        });

        return { specialties, proceduresBySpecialty };
    }, [results]);

    // Search handler with debouncing
    const handleSearch = useMemo(() => {
        let timeoutId: NodeJS.Timeout;

        return (searchQuery: string) => {
            clearTimeout(timeoutId);

            // Cancel any pending request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            if (!searchQuery.trim()) {
                setResults([]);
                setLoading(false);
                return;
            }

            setLoading(true);
            timeoutId = setTimeout(async () => {
                // Create a new AbortController for this request
                abortControllerRef.current = new AbortController();

                try {
                    const response = await searchProcedures(
                        searchQuery,
                        abortControllerRef.current.signal,
                    );
                    setResults(response);
                } catch (error) {
                    // Ignore errors from aborted requests
                    if (
                        error &&
                        typeof error === "object" &&
                        ("name" in error && error.name === "AbortError" ||
                         "code" in error && error.code === "ERR_CANCELED")
                    ) {
                        return;
                    }
                    console.error("Search failed:", error);
                    setResults([]);
                } finally {
                    setLoading(false);
                }
            }, 300);
        };
    }, []);

    // Cleanup: abort any pending request when component unmounts
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
        const value = event.target.value;
        setQuery(value);
        handleSearch(value);
        setOpen(true);
    }

    function handleResultClick(item: SearchResultItem) {
        addToHistory({
            type: item.type,
            label: item.label,
            slug: item.slug,
            specialty:
                item.type === "procedure" ? item.specialty?.name : undefined,
            specialtySlug:
                item.type === "procedure" ? item.specialty?.slug : undefined,
        });

        setOpen(false);
        setQuery("");
        setResults([]);

        // Navigate based on item type
        if (item.type === "specialty") {
            navigate(`/browse/${item.slug}`);
        } else if (item.procedure) {
            // Navigate to the specialty of the procedure
            const specialtySlug = item.specialty?.slug;
            if (specialtySlug) {
                navigate(`/browse/${specialtySlug}/${item.slug}`);
            } else {
                navigate(`/browse`);
            }
        }
    }

    function handleHistoryClick(item: SearchHistoryItem) {
        setOpen(false);
        setQuery("");

        // Navigate based on history item type
        if (item.type === "specialty") {
            navigate(`/browse/${item.slug}`);
        } else if (item.type === "procedure" && item.specialtySlug) {
            navigate(`/browse/${item.specialtySlug}/${item.slug}`);
        } else {
            // Fallback for procedures without specialty info
            navigate(`/browse`);
        }
    }

    function handleClearHistory(id: string) {
        removeFromHistory(id);
    }

    // Expose imperative handle for programmatic control
    useImperativeHandle(
        ref,
        () => ({
            appendToQuery: (char: string) => {
                setQuery((prev) => {
                    const newQuery = prev + char;
                    handleSearch(newQuery);
                    return newQuery;
                });
                setOpen(true);
                // Focus the input for screen reader accessibility
                // This ensures screen readers announce the search query changes
                inputRef.current?.focus();
            },
            removeLastChar: () => {
                setQuery((prev) => {
                    const newQuery = prev.slice(0, -1);
                    handleSearch(newQuery);
                    return newQuery;
                });
                setOpen(true);
                // Focus the input for screen reader accessibility
                inputRef.current?.focus();
            },
            focus: () => {
                inputRef.current?.focus();
            },
        }),
        [handleSearch],
    );

    return (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Box sx={{ position: "relative", ...sx }}>
                <TextField
                    {...props}
                    inputRef={inputRef}
                    fullWidth
                    value={query}
                    onChange={handleInputChange}
                    onFocus={() => setOpen(true)}
                    placeholder={t("search.placeholder", { ns: "browse" })}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    {loading ? (
                                        <CircularProgress 
                                            size={20} 
                                            aria-label={t("search.loading", { ns: "browse" })}
                                        />
                                    ) : (
                                        <SearchIcon />
                                    )}
                                </InputAdornment>
                            ),
                            endAdornment: query ? (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            setQuery("");
                                            setResults([]);
                                            inputRef.current?.focus();
                                        }}
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : undefined,
                        },
                    }}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "24px",
                            borderBottomLeftRadius: open ? 0 : "24px",
                            borderBottomRightRadius: open ? 0 : "24px",
                            bgcolor: "background.paper",
                            // remove the default outline transition so the dropdown and field stay visually consistent
                            transition: "none",
                        },
                        // force a consistent border colour and remove transitions on the outline
                        "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: alpha(theme.palette.divider, 0.5),
                            transition: "none",
                            // hide the bottom part of the outline when the popper is open so it visually joins the dropdown
                            borderBottom: open ? "none" : undefined,
                        },
                        // ensure focus and hover states keep the same border colour and no transition
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                            {
                                borderWidth: 1,
                                borderColor: alpha(theme.palette.divider, 0.5),
                                transition: "none",
                                borderBottom: open ? "none" : undefined,
                            },
                        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                            {
                                borderColor: alpha(theme.palette.divider, 0.5),
                                transition: "none",
                            },
                        // placeholder colour
                        "& input::placeholder": {
                            color: `${theme.palette.text.secondary} !important`,
                            opacity: 1,
                        },
                    }}
                />

                {open && (
                    <Box
                        ref={popperRef}
                        sx={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            right: 0,
                            zIndex: theme.zIndex.modal,
                        }}
                    >
                        <Paper
                            variant="outlined"
                            sx={{
                                maxHeight: 400,
                                overflow: "auto",
                                borderRadius: 3,
                                borderTopLeftRadius: 0,
                                borderTopRightRadius: 0,
                                border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                                borderTop: "0px solid",
                                bgcolor: "background.paper",
                                transition: "none",
                                boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                            }}
                        >
                            <List dense>
                                {results.length > 0 && (
                                    <SearchResults
                                        grouped={groupedResults}
                                        onSelect={handleResultClick}
                                    />
                                )}

                                {loading && (
                                    <ListItem>
                                        <ListItemText
                                            primary={t("search.searching", {
                                                ns: "browse",
                                            })}
                                            slotProps={{
                                                primary: {
                                                    variant: "body2",
                                                    color: "text.secondary",
                                                },
                                            }}
                                        />
                                    </ListItem>
                                )}

                                {!loading && query && results.length === 0 && (
                                    <ListItem>
                                        <ListItemText
                                            primary={t("search.no-results", {
                                                ns: "browse",
                                            })}
                                            slotProps={{
                                                primary: {
                                                    variant: "body2",
                                                    color: "text.secondary",
                                                },
                                            }}
                                        />
                                    </ListItem>
                                )}

                                {history.length > 0 &&
                                    (!query ||
                                        (!loading && results.length === 0)) && (
                                        <>
                                            {(results.length > 0 ||
                                                loading) && <Divider />}
                                            <SearchRecents
                                                history={history}
                                                onSelect={handleHistoryClick}
                                                onClear={handleClearHistory}
                                            />
                                        </>
                                    )}

                                {!query && history.length === 0 && !loading && (
                                    <ListItem>
                                        <ListItemText
                                            primary={t("search.no-results", {
                                                ns: "browse",
                                            })}
                                            slotProps={{
                                                primary: {
                                                    variant: "body2",
                                                    color: "text.secondary",
                                                },
                                            }}
                                        />
                                    </ListItem>
                                )}
                            </List>
                        </Paper>
                    </Box>
                )}
            </Box>
        </ClickAwayListener>
    );
});
